import React, { useEffect, useRef } from "react";
import {
	ScheduleEntryType,
	ScheduleEntryTypeCategory,
	ScheduleEntryTypeIcon,
} from "domain-entities";
import { useTranslation } from "Providers/TranslationProvider";
import AssignmentIcon from "@material-ui/icons/Assignment";
import FlagIcon from "@material-ui/icons/Flag";
import BeachAccessIcon from "@material-ui/icons/BeachAccess";
import CakeIcon from "@material-ui/icons/Cake";
import DirectionsCarIcon from "@material-ui/icons/DirectionsCar";
import EditIcon from "@material-ui/icons/Edit";
import LaptopIcon from "@material-ui/icons/Laptop";
import ListAltIcon from "@material-ui/icons/ListAlt";
import CameraAltIcon from "@material-ui/icons/CameraAlt";
import LocalFloristIcon from "@material-ui/icons/LocalFlorist";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import LockIcon from "@material-ui/icons/Lock";
import LoopIcon from "@material-ui/icons/Loop";
import MailIcon from "@material-ui/icons/Mail";
import PhoneIcon from "@material-ui/icons/Phone";
import PowerIcon from "@material-ui/icons/Power";
import ScheduleIcon from "@material-ui/icons/Schedule";
import WorkIcon from "@material-ui/icons/Work";
import VpnKeyIcon from "@material-ui/icons/VpnKey";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import GavelIcon from "@material-ui/icons/Gavel";
import HotelIcon from "@material-ui/icons/Hotel";
import HelpIcon from "@material-ui/icons/Help";
import WarningIcon from "@material-ui/icons/Warning";
import BatteryAlertIcon from "@material-ui/icons/BatteryAlert";
import NotificationsIcon from "@material-ui/icons/Notifications";
import BuildIcon from "@material-ui/icons/Build";
import { v4 } from "uuid";
import { useScheduleEntryTypes } from "./hooks/useScheduleEntryTypes";

enum DefaultScheduleEntryTypeName {
	VACATION = "VACATION",
	SICK = "SICK",
	DEFECT = "DEFECT",
	WORKSHOP = "WORKSHOP",
}

export function useTranslateScheduleEntryType() {
	const { t } = useTranslation();

	return (type: ScheduleEntryType) => {
		if (type.isDefault) {
			switch (type.name) {
				case DefaultScheduleEntryTypeName.VACATION:
					return t("ScheduleEntryTypeHelper_Type_Vacation");
				case DefaultScheduleEntryTypeName.SICK:
					return t("ScheduleEntryTypeHelper_Type_Sick");
				case DefaultScheduleEntryTypeName.DEFECT:
					return t("ScheduleEntryTypeHelper_Type_Defect");
				case DefaultScheduleEntryTypeName.WORKSHOP:
					return t("ScheduleEntryTypeHelper_Type_Workshop");
			}
		}

		return type.name;
	};
}

export function useTranslateScheduleEntryTypeCategory() {
	const { t } = useTranslation();

	return (category: ScheduleEntryTypeCategory) => {
		switch (category) {
			case ScheduleEntryTypeCategory.USER:
				return t("ScheduleEntryTypeHelper_Category_User");
			case ScheduleEntryTypeCategory.RESOURCE:
				return t("ScheduleEntryTypeHelper_Category_Resource");
		}
	};
}

export const SCHEDULE_ENTRY_TYPE_ICONS = {
	[ScheduleEntryTypeIcon.ASSIGNMENT]: AssignmentIcon,
	[ScheduleEntryTypeIcon.FLAG]: FlagIcon,
	[ScheduleEntryTypeIcon.BEACH]: BeachAccessIcon,
	[ScheduleEntryTypeIcon.CAKE]: CakeIcon,
	[ScheduleEntryTypeIcon.CAR]: DirectionsCarIcon,
	[ScheduleEntryTypeIcon.PEN]: EditIcon,
	[ScheduleEntryTypeIcon.LAPTOP]: LaptopIcon,
	[ScheduleEntryTypeIcon.LIST]: ListAltIcon,
	[ScheduleEntryTypeIcon.CAMERA]: CameraAltIcon,
	[ScheduleEntryTypeIcon.FLOWER]: LocalFloristIcon,
	[ScheduleEntryTypeIcon.OFFER]: LocalOfferIcon,
	[ScheduleEntryTypeIcon.LOCK]: LockIcon,
	[ScheduleEntryTypeIcon.LOOP]: LoopIcon,
	[ScheduleEntryTypeIcon.MAIL]: MailIcon,
	[ScheduleEntryTypeIcon.PHONE]: PhoneIcon,
	[ScheduleEntryTypeIcon.POWER]: PowerIcon,
	[ScheduleEntryTypeIcon.SCHEDULE]: ScheduleIcon,
	[ScheduleEntryTypeIcon.WORK]: WorkIcon,
	[ScheduleEntryTypeIcon.KEY]: VpnKeyIcon,
	[ScheduleEntryTypeIcon.CLOSED]: HighlightOffIcon,
	[ScheduleEntryTypeIcon.GAVEL]: GavelIcon,
	[ScheduleEntryTypeIcon.HOTEL]: HotelIcon,
	[ScheduleEntryTypeIcon.WARNING]: WarningIcon,
	[ScheduleEntryTypeIcon.BATTERY]: BatteryAlertIcon,
	[ScheduleEntryTypeIcon.BELL]: NotificationsIcon,
	[ScheduleEntryTypeIcon.TOOL]: BuildIcon,
};

export const FallbackScheduleEntryTypeIcon = HelpIcon;
export const DEFAULT_SCHEDULE_ENTRY_TYPE_ICON =
	ScheduleEntryTypeIcon.ASSIGNMENT;

export function getScheduleEntryTypeIcon(
	iconName: ScheduleEntryTypeIcon,
	size?: string
) {
	if (iconName in SCHEDULE_ENTRY_TYPE_ICONS) {
		const Icon = SCHEDULE_ENTRY_TYPE_ICONS[iconName];
		return <Icon style={{ fontSize: size }} />;
	} else {
		return <FallbackScheduleEntryTypeIcon style={{ fontSize: size }} />;
	}
}

const defaultScheduleEntryTypes: ScheduleEntryType[] = [
	{
		id: v4(),
		isDefault: true,
		name: DefaultScheduleEntryTypeName.SICK,
		category: ScheduleEntryTypeCategory.USER,
		icon: ScheduleEntryTypeIcon.HOTEL,
		colorTag: "COLOR10",
	},
	{
		id: v4(),
		isDefault: true,
		name: DefaultScheduleEntryTypeName.VACATION,
		category: ScheduleEntryTypeCategory.USER,
		icon: ScheduleEntryTypeIcon.BEACH,
		colorTag: "COLOR4",
	},
	{
		id: v4(),
		isDefault: true,
		name: DefaultScheduleEntryTypeName.DEFECT,
		category: ScheduleEntryTypeCategory.RESOURCE,
		icon: ScheduleEntryTypeIcon.WARNING,
		colorTag: "COLOR1",
	},
	{
		id: v4(),
		isDefault: true,
		name: DefaultScheduleEntryTypeName.WORKSHOP,
		category: ScheduleEntryTypeCategory.RESOURCE,
		icon: ScheduleEntryTypeIcon.TOOL,
		colorTag: "COLOR5",
	},
];

// TODO: Move this logic to the backend!
export function useInitDefaultScheduleEntryTypes() {
	const isInitializedRef = useRef(false);
	const isInitializingScheduleEntryTypes = useScheduleEntryTypes(
		(state) => state.isInitializing
	);
	const scheduleEntryTypesError = useScheduleEntryTypes((state) => state.error);

	useEffect(() => {
		if (isInitializingScheduleEntryTypes) return;
		if (scheduleEntryTypesError) return;
		if (isInitializedRef.current) return;

		isInitializedRef.current = true;

		const scheduleEntryTypes = useScheduleEntryTypes.getState().items;

		defaultScheduleEntryTypes.forEach((type) => {
			const defaultItem = scheduleEntryTypes.find(
				(item) => item.isDefault && item.name === type.name
			);

			if (!defaultItem) {
				useScheduleEntryTypes.getState().setItem(type);
			}
		});
	}, [isInitializingScheduleEntryTypes, scheduleEntryTypesError]);
}
